import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from "react";
import {
  StripePayment,
  AdyenPayment,
  AdyenCorpPers,
  TabGroup,
  Tab,
  PageLoader,
  Modal,
} from "@components";
import { PageTemplate, PageBody, PageFooter } from "../shared-components";
import { SkeletonLoader, SummaryButton } from "./components";
import { useOutletContext, useNavigate } from "react-router-dom";
import { cn } from "@utils";
import { useAdyenPageReload } from "../../hooks";

const PRPayByCard = () => {
  const {
    id,
    paymentRequestId,
    checkoutId,
    // core payment fields from outlet context
    merchant,
    amount,
    amountRequested,
    customerName,
    customerIsFinanced,
    customerId,
    branchId,
    salesOrderIds,
    paymentType,
    status,
    recipientEmail,
    source,
    transactionIds,
    reference,
    // card/BNPL flags
    cardPaymentAllowed,
    klarnaPaymentAllowed,
    amexPaymentAllowed,
    cardPaymentProvider,
    passOnCorporateCardFee,
    corporateCardSurchargePercent,
    // other flow/context
    statement,
    processingUrl,
    successUrl,
    summaryData,
    settlementDiscount,
    paymentDiscounts,
    setMethodStepData,
    methodStepData,
    flowId,
    // signed tokens for payment integrity validation
    paymentRequestToken,
    checkoutToken,
  } = useOutletContext();
  const navigate = useNavigate();
  const [processing, setProcessing] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isCorporate, setIsCorporate] = useState(false);
  const [currentAmount, setCurrentAmount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardIsCorporate, setCardIsCorporate] = useState(null);
  const [cardIsAmex, setCardIsAmex] = useState(false);
  const [cardBrand, setCardBrand] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [amexCorporateConfirmed, setAmexCorporateConfirmed] = useState(false);
  const [binLookupPending, setBinLookupPending] = useState(false);
  const stripeRef = useRef(null);
  const adyenRef = useRef(null);

  // Build rich paymentParameters object for Adyen/Stripe flows
  const cardPaymentParameters = {
    // identifiers
    id,
    paymentRequestId,
    checkoutId,
    merchantId: merchant.id,

    // monetary
    amount,
    amountRequested,

    // customer context
    customerName,
    customerId,
    customerIsFinanced,
    branchId,
    customerEmail: recipientEmail,

    // sales / reference
    salesOrderIds,
    paymentType,
    reference,

    // flags
    cardPaymentAllowed,
    klarnaPaymentAllowed,
    amexPaymentAllowed,

    // meta
    source,
    transactionIds,
    paymentDiscounts,

    // signed token forwarded unchanged to BFF for validation
    paymentRequestToken,
    checkoutToken,
  };

  // Coordinate one-time reload behaviour with the BNPL page so Adyen
  // components reset correctly when navigating between card and Klarna.
  useAdyenPageReload({
    visitedKey: "AdyenCardPageVisited",
    reloadOnKey: "AdyenBnplPageVisited",
  });

  useEffect(() => {
    if (!amount || Number(amount) === 0) {
      navigate(`/${flowId}/${id}/`);
    }
  }, [amount, id, flowId, navigate]);

  const handleCardNotAllowed = () => {
    console.error("Card payments are not allowed for this payment request");
    navigate(`/${flowId}/${id}/paymentmethod`);
  };

  const handleStorePaymentDetails = (overrideAmount) => {
    const finalAmount = overrideAmount ? { ...overrideAmount } : null;

    setMethodStepData({
      ...methodStepData,
      method: "paybycard",
      isCorporate,
      finalAmount,
      cardType: isCorporate ? "corporate" : "personal",
    });
  };

  const handlePayButtonClick = () => {
    if (!cardPaymentAllowed) {
      handleCardNotAllowed();
      return;
    }

    if (cardPaymentProvider === "Stripe") {
      // Adyen processing set to true in onSubmit handler
      handleStorePaymentDetails();
      setProcessing(true);
      stripeRef.current?.submitPayment();
    } else if (cardPaymentProvider === "Adyen") {
      adyenRef.current?.submitPayment();
    } else {
      console.error("Unknown cardPaymentProvider: " + cardPaymentProvider);
    }
  };

  const onStripeError = (err) => {
    if (err?.tokenExpired) { setSessionExpired(true); return; }
    setProcessing(false);
  };

  const onAdyenError = (err) => {
    if (err?.tokenExpired) { setSessionExpired(true); return; }
    setProcessing(false);
  };

  const setTypeToCorporate = (isCorporate) => () => {
    console.log("setTypeToCorporate", isCorporate);
    setIsCorporate(isCorporate);
    // Reset card type when switching tabs
    setCardIsCorporate(null);
    setCardBrand(null);
    setCardIsAmex(false);
    setErrorMessage("");
    setAmexCorporateConfirmed(false);
  };

  const handleAmountChange = useCallback((amountObj) => {
    if (amountObj && typeof amountObj === "object") {
      setCurrentAmount({ ...amountObj });
    }
  }, []);

  const handleCardTypeChange = useCallback((isCorporateCard) => {
    setCardIsCorporate(isCorporateCard);
  }, []);

  const handleAmexChange = useCallback((isAmex) => {
    setCardIsAmex(isAmex);
  }, []);

  // Handle brand validity updates from Adyen component
  const handleCardBrandChange = useCallback((brand) => {
    setCardBrand(brand);
  }, []);

  // Centralized validation logic
  useEffect(() => {
    let error = "";
    if (
      passOnCorporateCardFee &&
      cardIsAmex &&
      amexPaymentAllowed &&
      !isCorporate
    ) {
      error =
        "AMEX detected. To continue, please use VISA/Mastercard—or toggle the switch above to 'Corporate'";
    } else if (
      passOnCorporateCardFee &&
      cardIsCorporate === true &&
      !isCorporate
    ) {
      error =
        "Corporate card detected. To continue, please use a personal card—or toggle the switch above to 'Corporate'";
    } else if (
      passOnCorporateCardFee &&
      !cardIsAmex &&
      cardIsCorporate === false &&
      isCorporate
    ) {
      error =
        "Personal card detected. To continue, please use a corporate card—or toggle the switch above to 'Personal'";
    }

    setErrorMessage(error);
  }, [
    cardIsAmex,
    amexPaymentAllowed,
    cardIsCorporate,
    isCorporate,
    cardBrand,
    cardPaymentProvider,
    passOnCorporateCardFee,
  ]);

  const disablePayment = useMemo(() => {
    return (
      processing ||
      loading ||
      !!errorMessage ||
      (passOnCorporateCardFee &&
        ((cardIsAmex && isCorporate && !amexCorporateConfirmed) ||
          (!cardBrand && cardPaymentProvider === "Adyen") ||
          binLookupPending))
    );
  }, [
    processing,
    loading,
    errorMessage,
    cardIsAmex,
    isCorporate,
    amexCorporateConfirmed,
    cardBrand,
    cardPaymentProvider,
    binLookupPending,
    passOnCorporateCardFee,
  ]);

  return (
    <>
    <Modal isOpen={sessionExpired} onClose={() => navigate(`/${flowId}/${id}/`)} title="Session expired">
      <div className="p-5 flex flex-col gap-4">
        <p className="text-sm text-zinc-700">Payment session expired. Please start again.</p>
        <button
          onClick={() => navigate(`/${flowId}/${id}/`)}
          className="w-full rounded-lg bg-zinc-900 px-4 py-2.5 text-sm font-medium text-white hover:bg-zinc-700 cursor-pointer"
        >
          OK
        </button>
      </div>
    </Modal>
    <PageTemplate>
      <PageBody innerClassName="flex flex-col gap-4">
        <div className="px-5 lg:px-0">
          <h3 className="text-xl/8 font-semibold">Pay by card</h3>
          <h4 className="text-sm text-zinc-500">Enter your card details</h4>
        </div>
        {cardPaymentProvider === "Adyen" && passOnCorporateCardFee && (
          <div className="px-5 lg:px-0">
            <TabGroup>
              <Tab active={!isCorporate} onClick={setTypeToCorporate(false)}>
                <span className="text-sm text-zinc-900 flex flex-col items-center">
                  Personal
                  <span className="text-zinc-900 text-[10px] font-normal">
                    No fee
                  </span>
                </span>
              </Tab>
              <Tab active={isCorporate} onClick={setTypeToCorporate(true)}>
                <span className="text-sm text-zinc-900 flex flex-col items-center">
                  Corporate
                  <span className="text-zinc-900 text-[10px] font-normal">
                    {corporateCardSurchargePercent &&
                      `${corporateCardSurchargePercent}% fee`}
                  </span>
                </span>
              </Tab>
            </TabGroup>
          </div>
        )}
        <div className="px-5 lg:px-0 overflow-x-visible flex flex-col grow">
          <div className="grid py-5 px-4 bg-zinc-100 rounded-xl border border-zinc-300 relative">
            {/* Payment component (always mounted) */}
            <div className="row-start-1 col-start-1">
              {cardPaymentProvider === "Adyen" &&
                (passOnCorporateCardFee ? (
                  <AdyenCorpPers
                    isCorporate={isCorporate}
                    paymentParameters={cardPaymentParameters}
                    stripeAccountId={merchant.stripeId}
                    externalSubmitRef={adyenRef}
                    processingUrl={processingUrl}
                    successUrl={successUrl}
                    onError={onAdyenError}
                    onAmountChange={handleAmountChange}
                    setLoading={setLoading}
                    onCardTypeChange={handleCardTypeChange}
                    onAmexChange={handleAmexChange}
                    onCardBrandChange={handleCardBrandChange}
                    onCardNotAllowed={handleCardNotAllowed}
                    onStorePaymentDetails={handleStorePaymentDetails}
                    setProcessing={setProcessing}
                    disablePayment={disablePayment}
                    setBinLookupPending={setBinLookupPending}
                    corporateCardSurchargePercent={
                      corporateCardSurchargePercent
                    }
                  />
                ) : (
                  <AdyenPayment
                    paymentParameters={cardPaymentParameters}
                    stripeAccountId={merchant.stripeId}
                    externalSubmitRef={adyenRef}
                    processingUrl={processingUrl}
                    successUrl={successUrl}
                    onError={onAdyenError}
                    onAmountChange={handleAmountChange}
                    setLoading={setLoading}
                    onCardTypeChange={handleCardTypeChange}
                    onAmexChange={handleAmexChange}
                    onCardBrandChange={handleCardBrandChange}
                    onCardNotAllowed={handleCardNotAllowed}
                    onStorePaymentDetails={handleStorePaymentDetails}
                    setProcessing={setProcessing}
                    disablePayment={disablePayment}
                    setBinLookupPending={setBinLookupPending}
                  />
                ))}
              {cardPaymentProvider === "Stripe" && (
                <StripePayment
                  paymentParameters={cardPaymentParameters}
                  stripeAccountId={merchant.stripeId}
                  externalSubmitRef={stripeRef}
                  returnUrl={processingUrl}
                  onError={onStripeError}
                  onAmountChange={handleAmountChange}
                  setLoading={setLoading}
                />
              )}
            </div>

            <SkeletonLoader show={loading && cardPaymentProvider === "Adyen"} />
          </div>
        </div>
      </PageBody>

      <PageFooter>
        <div className="pb-7 space-y-2 hidden lg:block">
          <h2 className="text-xl font-medium">Payment summary</h2>
        </div>
        <SummaryButton
          errorMessage={errorMessage}
          currentAmount={currentAmount}
          passOnCorporateCardFee={passOnCorporateCardFee}
          isCorporate={isCorporate}
          cardIsAmex={cardIsAmex}
          amexCorporateConfirmed={amexCorporateConfirmed}
          setAmexCorporateConfirmed={setAmexCorporateConfirmed}
          handleContinue={handlePayButtonClick}
          processing={processing}
          loading={loading}
          merchant={merchant}
          hasStatement={!!statement}
          summaryData={summaryData}
          settlementDiscount={settlementDiscount}
          disablePayment={disablePayment}
        />
      </PageFooter>
    </PageTemplate>
    </>
  );
};

export default PRPayByCard;
